import { Injectable } from '@angular/core';
import { EntityConfigInterface } from '@rappider/rappider-components';
import {
  ActionBehavior,
  CrudViewColumnType, FormLayout, IconType,
  CrudViewFormItemType, ButtonType,
} from '@rappider/rappider-components/utils';
import { upperFirst } from 'lodash';
import { plural } from 'pluralize';
import { CrudFormInputItem, EntityConfigMetadata, EntityField } from '../models/entity-config.models';
import { environment } from '@environment';
import { HttpClient } from '@angular/common/http';
import { TokenService } from '@rappider/authentication/services';


@Injectable({
  providedIn: 'root'
})
export class EntityService {
  constructor(
    private http: HttpClient,
    private tokenService: TokenService
  ) {
    // Constructor intentionally empty - dependencies are injected via constructor parameters
  }

  /**
   * Get the default base API URL from environment configuration
   * @returns The base API URL string
   */
  getDefaultBaseApiUrl(): string {
    return environment.apiBaseUrl;
  }

  /**
   * Generate entity configuration based on metadata
   * @param metadata - The entity configuration metadata
   * @returns Generated entity configuration interface
   */
  generateEntityConfig(
    metadata: EntityConfigMetadata
  ): EntityConfigInterface {

    const lowerCaseName = metadata.name.toLowerCase();
    const pluralName = plural(metadata.name);
    const upperCasePluralName = pluralName.toUpperCase();
    const upperFirstSingular = upperFirst(lowerCaseName);
    const upperFirstPlural = upperFirst(pluralName);

    const editItemAction = {
      text: '',
      name: 'edit-item',
      behavior: ActionBehavior.CrudEditLink,
      icon: { name: 'far fa-edit', type: IconType.FontAwesome },
      tooltipText: 'Edit',
    };

    const deleteItemAction = {
      text: '',
      name: 'delete-item',
      behavior: ActionBehavior.Emit,
      icon: { name: 'far fa-trash', type: IconType.FontAwesome },
      popconfirmTitle: 'Are you sure you want to delete this item?',
      emitWithoutPopconfirm: false,
      tooltipText: 'Delete',
    };

    const defaultItemActions = [
      editItemAction,
      deleteItemAction
    ];

    const defaultEntityIconName = metadata.iconName || 'fa fa-regular fa-database';

    const listGridColumns = metadata.fields.map(field => {
      const commonProps = {
        title: field.title,
        fieldName: field.fieldName,
        dataType: field.dataType || 'string',
        visible: true,
      } as any;
      if (field.jsonSchema) {
        commonProps.jsonSchema = field.jsonSchema;
      }
      switch (field.displayFieldType) {
        case CrudViewColumnType.Link:
          return {
            ...commonProps,
            type: CrudViewColumnType.Link,
            routerLink: `/${metadata.apiPath}/detail/{{id}}`,
            linkBehavior: ActionBehavior.CrudDetailLink,
          };
        case CrudViewColumnType.TagList:
          return { ...commonProps, type: CrudViewColumnType.TagList };
        case CrudViewColumnType.Boolean:
          return { ...commonProps, type: CrudViewColumnType.Boolean };
        case CrudViewColumnType.Date:
          return { ...commonProps, type: CrudViewColumnType.Date, dateFormat: 'MM/DD/YYY' };
        case CrudViewColumnType.Number:
          return { ...commonProps, type: CrudViewColumnType.Number };
        case CrudViewColumnType.Text:
          return { ...commonProps, type: CrudViewColumnType.Text };
        case CrudViewColumnType.DateTime:
          return { ...commonProps, type: CrudViewColumnType.DateTime, dateFormat: 'MM/DD/YYY HH:mm' };
        case CrudViewColumnType.AutoDate:
          return { ...commonProps, type: CrudViewColumnType.AutoDate };
        case CrudViewColumnType.Tag:
          return { ...commonProps, type: CrudViewColumnType.Tag };
        case CrudViewColumnType.Icon:
          return { ...commonProps, type: CrudViewColumnType.Icon };
        case CrudViewColumnType.Image:
          return { ...commonProps, type: CrudViewColumnType.Image };
        case CrudViewColumnType.Currency:
          return { ...commonProps, type: CrudViewColumnType.Currency };
        case CrudViewColumnType.Rate:
          return { ...commonProps, type: CrudViewColumnType.Rate };
        case CrudViewColumnType.ActionLink:
          return { ...commonProps, type: CrudViewColumnType.ActionLink };
        case CrudViewColumnType.Code:
          return { ...commonProps, type: CrudViewColumnType.Code };
        case CrudViewColumnType.InputTemplate:
          return { ...commonProps, type: CrudViewColumnType.InputTemplate };
        default:
          return { ...commonProps, type: CrudViewColumnType.Text };
      }
    });

    const formItems = metadata.fields.map(field => {
      let formItem: CrudFormInputItem;
      const commonProps = {
        title: field.title,
        fieldName: field.fieldName,
        dataType: field.dataType || 'string',
        showRequiredSign: field.isRequired,
        disabled: field.disabled,
        default: field.default,
        text: field.text,
        options: [],
        inputComponentSize: { xs: 24, sm: 24, md: 6, lg: 4, xxl: 3 },
        section: field.section || 'Main Section',
        validators: field.formValidators || [],
        joyride: field.joyride,
        description: field.inputDescription,
        hint: field.hint,
        visible: field.visible,
        direction: field.direction,
        displayFormatFunction: field.displayFormatFunction,
      };

      switch (field.formFieldType) {
        case CrudViewFormItemType.TextBox:
          formItem = { ...commonProps, type: CrudViewFormItemType.TextBox };
          break;
        case CrudViewFormItemType.TextArea:
          formItem = { ...commonProps, type: CrudViewFormItemType.TextArea };
          break;
        case CrudViewFormItemType.Select:
          formItem = {
            ...commonProps,
            type: CrudViewFormItemType.Select,
            options: field.selectOptions?.map(opt => ({
              key: opt.key || opt.value,
              label: opt.label || opt.value,
              value: opt.value
            })) || [],
            settings: { searchable: true, mode: 'default', allowClear: true }
          };
          break;
        case CrudViewFormItemType.MonacoCodeEditor:
          formItem = {
            ...commonProps,
            type: CrudViewFormItemType.MonacoCodeEditor,
            editorOptions: {
              minimap: { enabled: false },
            },
            sizeSettings: { minHeight: '400px', maxHeight: '800px' },
            autoFormat: true,
            lineNumbers: 'on',
            folding: true,
          };
          if (field.dataType === 'object') {
            formItem.editorOptions = {
              minimap: { enabled: false },
              language: 'json',
              autoFormat: true,
              lineNumbers: 'on',
              folding: true,
            };
          }
          break;
        case CrudViewFormItemType.DocumentUpload:
          formItem = { ...commonProps, type: CrudViewFormItemType.DocumentUpload };
          break;
        case CrudViewFormItemType.DatePicker:
          formItem = { ...commonProps, type: CrudViewFormItemType.DatePicker };
          break;
        case CrudViewFormItemType.StringArray:
          formItem = {
            ...commonProps,
            type: CrudViewFormItemType.StringArray,
            displayFormatFunction: (value: string[]) => value.join(', '),
          };
          break;
        case CrudViewFormItemType.Number:
          formItem = { ...commonProps, type: CrudViewFormItemType.Number };
          break;
        case CrudViewFormItemType.Alert:
          formItem = { ...commonProps, type: CrudViewFormItemType.Alert };
          break;
        case CrudViewFormItemType.AssetPicker:
          formItem = { ...commonProps, type: CrudViewFormItemType.AssetPicker };
          break;
        case CrudViewFormItemType.BorderSettings:
          formItem = { ...commonProps, type: CrudViewFormItemType.BorderSettings };
          break;
        case CrudViewFormItemType.Button:
          formItem = { ...commonProps, type: CrudViewFormItemType.Button };
          break;
        case CrudViewFormItemType.CheckBox:
          formItem = { ...commonProps, type: CrudViewFormItemType.CheckBox };
          break;
        case CrudViewFormItemType.CheckboxList:
          formItem = { ...commonProps, type: CrudViewFormItemType.CheckboxList };
          break;
        case CrudViewFormItemType.CheckboxTable:
          formItem = { ...commonProps, type: CrudViewFormItemType.CheckboxTable };
          break;
        case CrudViewFormItemType.Code:
          formItem = { ...commonProps, type: CrudViewFormItemType.Code };
          break;
        case CrudViewFormItemType.CodeMirror:
          formItem = { ...commonProps, type: CrudViewFormItemType.CodeMirror, settings: {} };
          break;
        case CrudViewFormItemType.ColorPicker:
          formItem = { ...commonProps, type: CrudViewFormItemType.ColorPicker };
          break;
        case CrudViewFormItemType.Date:
          formItem = { ...commonProps, type: CrudViewFormItemType.Date };
          break;
        case CrudViewFormItemType.DateTime:
          formItem = { ...commonProps, type: CrudViewFormItemType.DateTime };
          break;
        case CrudViewFormItemType.DateTimePicker:
          formItem = { ...commonProps, type: CrudViewFormItemType.DateTimePicker };
          break;
        case CrudViewFormItemType.DimensionSelect:
          formItem = { ...commonProps, type: CrudViewFormItemType.DimensionSelect };
          break;
        case CrudViewFormItemType.DynamicArrayForm:
          formItem = { ...commonProps, type: CrudViewFormItemType.DynamicArrayForm };
          break;
        case CrudViewFormItemType.EditFormConfigInput:
          formItem = { ...commonProps, type: CrudViewFormItemType.EditFormConfigInput };
          break;
        case CrudViewFormItemType.FormBuilder:
          formItem = { ...commonProps, type: CrudViewFormItemType.FormBuilder };
          break;
        case CrudViewFormItemType.GridBuilder:
          formItem = { ...commonProps, type: CrudViewFormItemType.GridBuilder };
          break;
        case CrudViewFormItemType.HTMLEditor:
          formItem = { ...commonProps, type: CrudViewFormItemType.HTMLEditor };
          break;
        case CrudViewFormItemType.IconPicker:
          formItem = { ...commonProps, type: CrudViewFormItemType.IconPicker };
          break;
        case CrudViewFormItemType.IconPickerTwo:
          formItem = { ...commonProps, type: CrudViewFormItemType.IconPickerTwo };
          break;
        case CrudViewFormItemType.IconPickerTwoWrapper:
          formItem = { ...commonProps, type: CrudViewFormItemType.IconPickerTwoWrapper };
          break;
        case CrudViewFormItemType.Image:
          formItem = { ...commonProps, type: CrudViewFormItemType.Image };
          break;
        case CrudViewFormItemType.ImageUpload:
          formItem = { ...commonProps, type: CrudViewFormItemType.ImageUpload };
          break;
        case CrudViewFormItemType.InlineRowForm:
          formItem = { ...commonProps, type: CrudViewFormItemType.InlineRowForm };
          break;
        case CrudViewFormItemType.InputGroup:
          formItem = { ...commonProps, type: CrudViewFormItemType.InputGroup };
          break;
        case CrudViewFormItemType.InputTemplate:
          formItem = { ...commonProps, type: CrudViewFormItemType.InputTemplate };
          break;
        case CrudViewFormItemType.JsonArray:
          formItem = { ...commonProps, type: CrudViewFormItemType.JsonArray };
          break;
        case CrudViewFormItemType.JsonInput:
          formItem = { ...commonProps, type: CrudViewFormItemType.JsonInput };
          break;
        case CrudViewFormItemType.Label:
          formItem = { ...commonProps, type: CrudViewFormItemType.Label };
          break;
        case CrudViewFormItemType.LabelFunction:
          formItem = { ...commonProps, type: CrudViewFormItemType.LabelFunction };
          break;
        case CrudViewFormItemType.List:
          formItem = { ...commonProps, type: CrudViewFormItemType.List };
          break;
        case CrudViewFormItemType.ListGridConfigInput:
          formItem = { ...commonProps, type: CrudViewFormItemType.ListGridConfigInput };
          break;
        case CrudViewFormItemType.ListGridDataInput:
          formItem = { ...commonProps, type: CrudViewFormItemType.ListGridDataInput };
          break;
        case CrudViewFormItemType.MarkDown:
          formItem = { ...commonProps, type: CrudViewFormItemType.MarkDown };
          break;
        case CrudViewFormItemType.Object:
          formItem = { ...commonProps, type: CrudViewFormItemType.Object };
          break;
        case CrudViewFormItemType.ObjectButton:
          formItem = { ...commonProps, type: CrudViewFormItemType.ObjectButton };
          break;
        case CrudViewFormItemType.PhoneNumber:
          formItem = { ...commonProps, type: CrudViewFormItemType.PhoneNumber };
          break;
        case CrudViewFormItemType.Previewer:
          formItem = { ...commonProps, type: CrudViewFormItemType.Previewer };
          break;
        case CrudViewFormItemType.Radio:
          formItem = {
            ...commonProps, type: CrudViewFormItemType.Radio, options: field.options?.map(option => ({
              key: option.key || option.value,
              value: option.value
            })) || []
          };
          break;
        case CrudViewFormItemType.RadioGroup:
          formItem = { ...commonProps, type: CrudViewFormItemType.RadioGroup };
          break;
        case CrudViewFormItemType.Rate:
          formItem = { ...commonProps, type: CrudViewFormItemType.Rate };
          break;
        case CrudViewFormItemType.RichTextEditor:
          formItem = { ...commonProps, type: CrudViewFormItemType.RichTextEditor };
          break;
        case CrudViewFormItemType.RowForm:
          formItem = { ...commonProps, type: CrudViewFormItemType.RowForm };
          break;
        case CrudViewFormItemType.RowSelect:
          formItem = { ...commonProps, type: CrudViewFormItemType.RowSelect };
          break;
        case CrudViewFormItemType.SelectCard:
          formItem = { ...commonProps, type: CrudViewFormItemType.SelectCard };
          break;
        case CrudViewFormItemType.ShadowSettings:
          formItem = { ...commonProps, type: CrudViewFormItemType.ShadowSettings };
          break;
        case CrudViewFormItemType.Slider:
          formItem = { ...commonProps, type: CrudViewFormItemType.Slider };
          break;
        case CrudViewFormItemType.Spacing:
          formItem = { ...commonProps, type: CrudViewFormItemType.Spacing };
          break;
        case CrudViewFormItemType.Switch:
          formItem = { ...commonProps, type: CrudViewFormItemType.Switch };
          break;
        case CrudViewFormItemType.Table:
          formItem = { ...commonProps, type: CrudViewFormItemType.Table };
          break;
        case CrudViewFormItemType.TagBox:
          formItem = { ...commonProps, type: CrudViewFormItemType.TagBox };
          break;
        case CrudViewFormItemType.TagInput:
          formItem = { ...commonProps, type: CrudViewFormItemType.TagInput };
          break;
        case CrudViewFormItemType.TimePicker:
          formItem = { ...commonProps, type: CrudViewFormItemType.TimePicker };
          break;
        case CrudViewFormItemType.TreeSelect:
          formItem = { ...commonProps, type: CrudViewFormItemType.TreeSelect };
          break;
        case CrudViewFormItemType.FileUpload:
          formItem = { ...commonProps, type: CrudViewFormItemType.FileUpload };
          formItem.config = {
            acceptedFileTypes: field.config?.acceptedFileTypes || '.*',
            uploadText: field.config?.uploadText || 'Click or drag a file to this area to upload'
          };
          break;
        default:
          formItem = { ...commonProps, type: CrudViewFormItemType.TextBox };
          break;
      }
      return formItem;
    });

    const formSections = Array.from(new Set(metadata.fields.map(field => field.section || 'Main Section'))).map(sectionName => ({
      title: sectionName,
      key: sectionName.toLowerCase().replace(/\s/g, ''),
      columns: 2
    }));

    const blacklistedFieldsForUpdateForms = [
      'id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'deletedAt', 'deletedBy', 'projectId', 'projectFiles', 'createdDate'
    ];

    const blacklistedFieldsForDetailView = [
      'id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'deletedAt', 'deletedBy', 'projectId'
    ];

    const blacklistedFieldsForCreateForms = [
      'id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'deletedAt', 'deletedBy', 'projectId', 'projectFiles', 'createdDate'
    ];

    const updateFormItems = formItems.filter(item => !blacklistedFieldsForUpdateForms.includes(item.fieldName || ''));
    const detailViewItems = formItems.filter(item => !blacklistedFieldsForDetailView.includes(item.fieldName || ''));
    const createFormItems = formItems.filter(item => !blacklistedFieldsForCreateForms.includes(item.fieldName || ''));

    const entityConfig: EntityConfigInterface = {
      id: metadata.name,
      name: metadata.name,
      title: metadata.title,
      description: metadata.description,
      iconName: defaultEntityIconName,
      config: {
        listMode: metadata.listMode,
        relationsConfig: metadata?.relationsConfig || [],
        listGridConfig: metadata.savedGridConfig ? {
          ...{
            columns: listGridColumns,
            itemActions: defaultItemActions,
            listActions: metadata?.listActions ?? [],
            listActionsLength: metadata?.listActionsLength ?? null,
            handleVisibilityFn: metadata.handleVisibilityFn,
            searchable: true,
            defaultSearchField: metadata.titleFieldName ||
              metadata.fields[0]?.fieldName || 'title',
            multipleSearchFields: metadata.searchFields || metadata.fields.map(field => field.fieldName),
            showSearchWithAiButton: metadata.showSearchWithAiButton || true,
          },
          ...metadata.savedGridConfig
        } : {
          columns: listGridColumns,
          itemActions: defaultItemActions,
          listActions: metadata?.listActions ?? [],
          listActionsLength: metadata?.listActionsLength ?? null,
          handleVisibilityFn: metadata.handleVisibilityFn,
          searchable: true,
          defaultSearchField: metadata.titleFieldName || metadata.fields[0]?.fieldName || 'title',
          multipleSearchFields: metadata.searchFields || metadata.fields.map(field => field.fieldName),
          showSearchWithAiButton: metadata.showSearchWithAiButton || true,
        },
        cardListConfig: {
          titleFieldName: metadata?.titleFieldName || metadata.fields[0]?.fieldName || 'id',
          descriptionFieldName: metadata.fields?.find(f => f.fieldName === 'description')?.fieldName ||
            metadata.fields[0]?.fieldName || 'id',
          subtitleFieldNames: metadata.subtitleFieldNames || metadata.fields.slice(1, 4).map(field => field.fieldName),
          getSubTitlesFunction: metadata.getSubTitlesFunction || undefined,
          getDescriptionFunction: metadata.getDescriptionFunction || undefined,
          alertConfig: metadata.alertConfig || undefined,
          cardItemClickBehavior: ActionBehavior.Emit,
          cardItemRouterLink: `/${metadata.apiPath}/detail/{{id}}`,
          deleteItemActionButton: deleteItemAction,
          editItemActionButton: editItemAction,
          iconFn: metadata.iconFn,
          showSearchWithAiButton: metadata.showSearchWithAiButton || true,
        },
        createItemFormConfig: {
          items: createFormItems,
          formLayout: FormLayout.Vertical,
          sections: formSections,
          submitButton: { text: `Add ${metadata.title || metadata.name}`, visible: true, size: 'default', type: ButtonType.Primary },
        },
        updateItemFormConfig: {
          items: updateFormItems,
          formLayout: FormLayout.Vertical,
          sections: formSections,
          submitButton: { text: `Update ${metadata.title || metadata.name}`, visible: true, size: 'default', type: ButtonType.Primary },
        },
        detailViewConfig: {
          items: detailViewItems,
          sections: formSections,
          // displays a column number selector to change the number of columns in the detail view
          displayColumnNumberSelectorForDetailView: true
        },
        idFieldName: 'id',
        titleFieldName: metadata.titleFieldName ||
          metadata.fields[0]?.fieldName || 'id',
        entityDataListSelector: pluralName,
        entityDataActiveItemSelector: `active${upperFirstSingular}`,
        listItemsTitle: upperFirstPlural,
        createItemTitle: `Create ${metadata.title || upperFirstSingular}`,
        updateItemTitle: `Edit ${metadata.title || upperFirstSingular}`,
        deleteItemTitle: `Delete ${metadata.title || upperFirstSingular}`,
        createEntityActionType: `[${upperCasePluralName}] Create${upperFirstSingular}`,
        updateEntityActionType: `[${upperCasePluralName}] Update${upperFirstSingular}`,
        deleteEntityActionType: `[${upperCasePluralName}] Delete${upperFirstSingular}`,
        getEntityItemsActionType: `[${upperCasePluralName}] Get${upperFirstPlural}`,
        getEntityItemByIdActionType: `[${upperCasePluralName}] Get${upperFirstSingular}ById`,
        dataFetchedFrom: metadata.dataFetchedFrom || 'loopback-api',
        alertConfig: metadata.alertConfig,
        entityDataCreateTransformFunction: metadata.entityDataCreateTransformFunction,
        entityDataUpdateTransformFunction: metadata.entityDataUpdateTransformFunction,
        entityDataGetTransformFunction: metadata.entityDataGetTransformFunction,
        apiConfig: {
          baseUrl: metadata.apiBaseUrl || this.getDefaultBaseApiUrl(),
          getAll: {
            url: metadata.apiPath || pluralName.toLowerCase(),
            filter: metadata.apiGetFilter || null,
          },
          getById: {
            url: `${metadata.apiPath || pluralName.toLowerCase()}/{{id}}`,
          },
          create: {
            url: `${metadata.apiPath || pluralName.toLowerCase()}`,
          },
          updateById: {
            url: `${metadata.apiPath || pluralName.toLowerCase()}/{{id}}`,
          },
          deleteById: {
            url: `${metadata.apiPath || pluralName.toLowerCase()}/{{id}}`,
          },
        }
      }
    };

    return entityConfig;
  }

  /**
   * Convert a string to camelCase format
   * @param str - The string to convert
   * @returns The camelCase formatted string
   */
  convertToCamelCase(str: string): string {
    return str
      .replace(
        /(?:^\w|[A-Z]|\b\w)/g,
        (match, index) => index === 0 ? match.toLowerCase() : match.toUpperCase()
      )
      .replace(/\s+/g, '');
  }

  /**
   * Convert a string to PascalCase format
   * @param str - The string to convert
   * @returns The PascalCase formatted string
   */
  convertToPascalCase(str: string): string {
    if (!str) {
      return '';
    }

    return str
      .replace(/([-_.\s]+)|([A-Z])/g, (match, separator, letter, offset) => {
        if (letter && offset > 0) {
          return ' ' + letter;
        }
        if (separator) {
          return ' ';
        }
        return match;
      })
      .replace(/(?:^\w|\b\w)/g, (match) => match.toUpperCase())
      .replace(/\s+/g, '');
  }

  /**
   * Convert a string to Title Case format
   * @param str - The string to convert
   * @returns The Title Case formatted string
   */
  convertToTitleCase(str: string): string {
    return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match) => match.charAt(0).toUpperCase() + match.slice(1).toLowerCase());
  }

  /**
   * Generate example data for an entity based on its configuration metadata
   * Creates 5 sample records with realistic example values for each field
   * @param metadata - The entity configuration metadata containing field definitions
   * @returns Array of 5 example data objects
   */
  generateExampleData(metadata: EntityConfigMetadata): any[] { // eslint-disable-line @typescript-eslint/no-explicit-any
    const exampleRecords: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any

    // Generate 5 example records
    for (let i = 1; i <= 5; i++) {
      const record: any = {}; // eslint-disable-line @typescript-eslint/no-explicit-any
      record.id = this.generateExampleId(i);
      metadata.fields.forEach(field => {
        if (field.isMixinProperty) {
          return;
        }

        const fieldName = field.fieldName;
        record[fieldName] = this.generateExampleValueForField(field, i);
      });

      exampleRecords.push(record);
    }

    return exampleRecords;
  }

  /**
   * Generate an example ID for a record
   * @param index - The index of the record (1-5)
   * @returns A UUID-like string or simple ID
   */
  private generateExampleId(index: number): string {
    // Generate a simple UUID-like format for example data
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 10000);
    return `example-${timestamp}-${random}-${index}`;
  }

  /**
   * Generate an example value for a specific field based on its type and characteristics
   * @param field - The field definition
   * @param index - The index of the record being generated (1-5)
   * @returns An appropriate example value for the field
   */
  private generateExampleValueForField(field: EntityField, index: number): any { // eslint-disable-line @typescript-eslint/no-explicit-any
    const fieldName = field.fieldName.toLowerCase();
    const fieldTitle = field.title || fieldName;
    const dataType = (field.dataType || 'string').toLowerCase();

    // Handle special field name patterns
    if (fieldName.includes('email')) {
      return `example${index}@sample.com`;
    }

    if (fieldName.includes('phone') || fieldName.includes('tel')) {
      return `+1-555-${String(1000 + index).padStart(4, '0')}`;
    }

    if (fieldName.includes('url') || fieldName.includes('website')) {
      return `https://example${index}.com`;
    }

    if (fieldName.includes('description') || fieldName.includes('notes') || fieldName.includes('comment')) {
      return `This is an example ${fieldTitle} for sample record ${index}. It demonstrates how this field would appear with actual data.`;
    }

    if (fieldName.includes('name') && !fieldName.includes('file') && !fieldName.includes('user')) {
      return `Sample ${fieldTitle} ${index}`;
    }

    if (fieldName.includes('title')) {
      return `Example ${fieldTitle} ${index}`;
    }

    if (fieldName.includes('status')) {
      const statuses = ['Active', 'Inactive', 'Pending', 'Completed', 'Draft'];
      return statuses[(index - 1) % statuses.length];
    }

    if (fieldName.includes('category') || fieldName.includes('type')) {
      const categories = ['Type A', 'Type B', 'Type C', 'Type D', 'Type E'];
      return categories[(index - 1) % categories.length];
    }

    if (fieldName.includes('price') || fieldName.includes('cost') || fieldName.includes('amount')) {
      return parseFloat((Math.random() * 1000 + index * 100).toFixed(2));
    }

    if (fieldName.includes('quantity') || fieldName.includes('count') || fieldName.includes('number')) {
      return index * 10;
    }

    switch (dataType) {
      case 'number':
      case 'integer':
      case 'float':
      case 'double':
        return index * (fieldName.includes('id') ? 1 : 10);
      case 'boolean':
        return index % 2 === 0;
      case 'date':
      case 'datetime':
        const baseDate = new Date();
        baseDate.setDate(baseDate.getDate() - (6 - index));
        return dataType === 'date' ? baseDate.toISOString().split('T')[0] : baseDate.toISOString();
      case 'json':
      case 'object':
        return { example: `value${index}`, index: index };
      case 'array':
        return [`item${index}-1`, `item${index}-2`, `item${index}-3`];
      case 'string':
      default:
        if (field.selectOptions && field.selectOptions.length > 0) {
          const optionIndex = (index - 1) % field.selectOptions.length;
          return field.selectOptions[optionIndex].value;
        }
        return `Sample ${fieldTitle} ${index}`;
    }
  }
}
